<template>
    <div class="case-docket">
        <!-- Page Header -->
        <div class="page-head">
            <h2 class="title">จัดการชั้นศาล</h2>
            <p class="sub">เพิ่ม / แก้ไข / ลบ รายชื่อชั้นศาลที่ใช้ในระบบคดี</p>
        </div>

        <!-- ============ Toolbar ============ -->
        <div class="toolbar">
            <input v-model="search" class="search" placeholder="🔍 ค้นหา: ชื่อชั้นศาล" />
            <button class="btn primary" :disabled="!authorize_add" @click="openCreate">
                + เพิ่มชั้นศาล
            </button>
        </div>

        <!-- ============ ตารางรายการ ============ -->
        <table>
            <thead>
                <tr>
                    <th style="width:70px">#</th>
                    <th>ชื่อชั้นศาล</th>
                    <th style="width:180px">วันที่สร้าง</th>
                    <th style="width:120px">Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td colspan="4" class="empty">กำลังโหลดข้อมูล…</td>
                </tr>
                <tr v-else-if="filteredItems.length === 0">
                    <td colspan="4" class="empty">
                        {{ search ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีข้อมูลชั้นศาล' }}
                    </td>
                </tr>
                <tr v-for="(item, index) in filteredItems" :key="item.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.name }}</td>
                    <td><span class="hint">{{ formatDate(item.created_at) }}</span></td>
                    <td>
                        <div class="acts">
                            <span class="ic" title="แก้ไข" :class="{ disabled: !authorize_edit }"
                                @click="authorize_edit && openEdit(item)">✎</span>
                            <span class="ic danger" title="ลบ" :class="{ disabled: !authorize_del }"
                                @click="authorize_del && openDelete(item)">🗑</span>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- ============ Dialog: เพิ่ม / แก้ไข ============ -->
        <v-dialog v-model="dialog" max-width="520" persistent>
            <v-card class="docket-panel">
                <div class="dialog-head">
                    {{ isEdit ? 'แก้ไขชั้นศาล' : 'เพิ่มชั้นศาล' }}
                </div>
                <v-form ref="form" v-model="formValid">
                    <div class="pa-6 pt-4">
                        <div class="field-label">
                            ชื่อชั้นศาล (Name) <span class="req">*</span>
                        </div>
                        <v-text-field v-model="editItem.name" placeholder="เช่น ศาลชั้นต้น" :rules="[rules.required]"
                            outlined dense hide-details="auto" autofocus />
                    </div>

                    <div class="footer-actions" style="margin-top:0; padding:0 24px 22px;">
                        <v-btn color="primary" class="navy-btn equal-btn" depressed :loading="saving" @click="onSave"
                            width="120px">
                            SAVE
                        </v-btn>
                        <v-btn outlined class="equal-btn" :disabled="saving" @click="closeDialog"
                            width="120px">CANCEL</v-btn>
                    </div>
                </v-form>
            </v-card>
        </v-dialog>

        <!-- ============ Dialog: ยืนยันการลบ ============ -->
        <v-dialog v-model="dialogDelete" max-width="420" persistent>
            <v-card class="docket-panel">
                <div class="dialog-head danger">ยืนยันการลบ</div>
                <div class="pa-6">
                    ต้องการลบชั้นศาล <b>"{{ editItem.name }}"</b> ใช่หรือไม่?
                    <div class="note-inline mt-3">
                        การลบไม่สามารถย้อนกลับได้ และอาจกระทบคดีที่อ้างถึงชั้นศาลนี้
                    </div>
                </div>
                <div class="footer-actions" style="margin-top:0; padding:0 24px 22px;">
                    <v-btn color="#c0392b" dark class="px-8" depressed :loading="deleting" @click="onDelete">
                        ลบ
                    </v-btn>
                    <v-btn outlined class="px-8" :disabled="deleting" @click="dialogDelete = false">ยกเลิก</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import Swal from "sweetalert2";

export default {
    name: "Litigationcourtlevel",
    data() {
        return {
            search: "",
            loading: false,
            saving: false,
            deleting: false,

            items: [],

            dialog: false,
            dialogDelete: false,
            isEdit: false,
            formValid: false,

            editItem: { id: null, name: "" },
            defaultItem: { id: null, name: "" },

            rules: {
                required: (v) => !!v || "จำเป็นต้องกรอก",
            },

            // authorize
            userId: null,
            authorize_id: null,
            authorize_view: false,
            authorize_add: false,
            authorize_edit: false,
            authorize_del: false,
        };
    },

    computed: {
        filteredItems() {
            if (!this.search) return this.items;
            const kw = this.search.toLowerCase();
            return this.items.filter(
                (i) => i.name && i.name.toLowerCase().includes(kw)
            );
        },
    },

    async beforeCreate() {
        this.$store.state.navMenu = true;
    },

    async mounted() {

        try {
            await api.checkVersion();

            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

            // ----------------- Check Authorize ID ---------------------------
            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$store.state.global_dialog = true;
                this.setupAlertDialog(
                    true,
                    "Authorize Failed!!!",
                    "Please Logout And Login Again!!!",
                    "text-h5 red--text text-center"
                );
                this.$router.back();
                return;
            }

            // ----------------- Resolve Current Route Path -------------------
            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.replace("/", "");

            // ----------------- Fetch Authorization --------------------------
            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];

            this.authorize_view = permission.smd_view >= 1;
            this.authorize_add = permission.smd_add >= 1;
            this.authorize_edit = permission.smd_edit >= 1;
            this.authorize_del = permission.smd_del >= 1;

            if (!this.authorize_view) {
                this.$router.back();
                return;
            }

            // ----------------- Check Login Status ---------------------------
            if (this.userId && api.isLoggedIn()) {
                await this.loadData();
            } else {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
            }
        } catch (error) {
            console.error("mounted() error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
        } finally {
            this.$hideLoader();
        }
    },

    methods: {
        // ----------------- Load list -----------------
        async loadData() {
            this.loading = true;
            try {
                const res = await api.dispute_resolution_getall_courtlevel();
                this.items = res.data || [];
            } catch (error) {
                console.error("loadData() error:", error);
            } finally {
                this.loading = false;
            }
        },

        formatDate(d) {
            if (!d) return "—";
            const date = new Date(d);
            if (isNaN(date)) return "—";
            return date.toLocaleDateString("th-TH", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },

        // ----------------- Open dialogs -----------------
        openCreate() {
            this.isEdit = false;
            this.editItem = Object.assign({}, this.defaultItem);
            this.dialog = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        openEdit(item) {
            this.isEdit = true;
            this.editItem = Object.assign({}, item);
            this.dialog = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        openDelete(item) {
            this.editItem = Object.assign({}, item);
            this.dialogDelete = true;
        },
        closeDialog() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editItem = Object.assign({}, this.defaultItem);
            });
        },
        isDuplicateName(name, excludeId = null) {
            const normalized = (name || "").trim().toLowerCase();
            if (!normalized) return false;
            return this.items.some(
                (i) =>
                    i.name &&
                    i.name.trim().toLowerCase() === normalized &&
                    i.id !== excludeId
            );
        },
        // ----------------- Save (create / update) -----------------
        async onSave() {
            if (!this.$refs.form.validate()) return;
            const isDup = this.isDuplicateName(
                this.editItem.name,
                this.isEdit ? this.editItem.id : null
            );
            if (isDup) {
                Swal.fire({
                    icon: "warning",
                    title: "ชื่อศาลซ้ำ",
                    text: "มีชื่อศาลนี้อยู่ในระบบแล้ว กรุณาใช้ชื่ออื่น",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                return;
            }
            this.saving = true;
            try {
                await this.$showLoader()
                if (this.isEdit) {
                    var datalist = {
                        name: this.editItem.name,
                        user_update: localStorage.getItem(server.USER_ID),
                    }
                    await api.dispute_resolution_update_courtlevel(this.editItem.id, datalist);
                } else {
                    var datalist = {
                        name: this.editItem.name,
                        user_create: localStorage.getItem(server.USER_ID),
                        user_update: localStorage.getItem(server.USER_ID),
                    }
                    await api.dispute_resolution_add_courtlevel(datalist)
                }

                this.closeDialog();
                await this.loadData();

                Swal.fire({
                    icon: "success",
                    title: this.isEdit ? "แก้ไขสำเร็จ" : "เพิ่มสำเร็จ",
                    text: this.isEdit ? "บันทึกการแก้ไขเรียบร้อยแล้ว" : "เพิ่มชั้นศาลใหม่เรียบร้อยแล้ว",
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });

            } catch (error) {
                console.error("onSave() error:", error);
                Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            } finally {
                await this.$hideLoader()
                this.saving = false;
            }
        },

        // ----------------- Delete -----------------
        async onDelete() {
            await this.$showLoader()
            this.deleting = true;
            try {
                await api.dispute_resolution_delete_courtlevel(this.editItem.id);
                this.dialogDelete = false;
                await this.loadData();

                Swal.fire({
                    icon: "success",
                    title: "ลบสำเร็จ",
                    text: "ลบข้อมูลชั้นศาลเรียบร้อยแล้ว",
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });

            } catch (error) {
                console.error("onDelete() error:", error);
                Swal.fire({
                    icon: "error",
                    title: "ลบไม่สำเร็จ",
                    text: "ไม่สามารถลบข้อมูลได้ อาจมีคดีที่อ้างถึงชั้นศาลนี้อยู่",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            } finally {
                await this.$hideLoader()
                this.deleting = false;
            }
        },
    },
};
</script>

<style scoped>
.case-docket {
    max-width: 1180px;
    margin: 0 auto;
    padding: 22px;
    color: #1f2733;
    font-size: 14px;
}

/* header */
.page-head .title {
    font-size: 20px;
    margin: 0 0 4px;
    font-weight: 600;
}

.page-head .sub {
    color: #6b7785;
    margin: 0 0 18px;
}

/* toolbar (จาก wireframe) */
.toolbar {
    background: #fff;
    border: 1px solid #d4d9e0;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 14px;
}

.toolbar .search {
    flex: 1;
    min-width: 200px;
    padding: 9px 11px;
    border: 1px solid #d4d9e0;
    border-radius: 6px;
    font-size: 13px;
    background: #fff;
    font-family: inherit;
}

.toolbar .search:focus {
    outline: none;
    border-color: #1b7a99;
}

.btn {
    border: none;
    border-radius: 6px;
    padding: 9px 16px;
    font-size: 13px;
    cursor: pointer;
}

.btn.primary {
    background: #0e3a5f;
    color: #fff;
}

.btn.primary:disabled {
    background: #9aa7b5;
    cursor: not-allowed;
}

/* table (จาก wireframe) */
table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #d4d9e0;
    border-radius: 10px;
    overflow: hidden;
}

th,
td {
    padding: 11px 12px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid #d4d9e0;
}

th {
    background: #eef2f7;
    color: #6b7785;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
}

tr:last-child td {
    border-bottom: none;
}

tbody tr:hover td {
    background: #f9fbfd;
}

td.empty {
    text-align: center;
    color: #6b7785;
    padding: 26px 12px;
}

.hint {
    font-size: 11px;
    color: #6b7785;
}

/* action icons (จาก wireframe .ic) */
.acts {
    display: flex;
    gap: 6px;
}

.ic {
    width: 30px;
    height: 30px;
    border: 1px solid #d4d9e0;
    border-radius: 6px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s;
}

.ic:hover {
    border-color: #1b7a99;
    background: #f1f8fb;
}

.ic.danger:hover {
    border-color: #c0392b;
    background: #fdf1f0;
}

.ic.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}

/* field labels */
.field-label {
    font-size: 12.5px;
    color: #37424f;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 18px;
}

.field-label .req {
    color: #c0392b;
}

/* dialog */
.docket-panel {
    border-color: #d4d9e0 !important;
    border-radius: 10px;
}

.dialog-head {
    background: #0e3a5f;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    padding: 14px 24px;
    border-radius: 10px 10px 0 0;
}

.dialog-head.danger {
    background: #c0392b;
}

/* inline note */
.note-inline {
    font-size: 11.5px;
    color: #8a6d00;
    background: #fff8e6;
    border-left: 3px solid #e7c65a;
    padding: 6px 10px;
    border-radius: 4px;
}

/* footer */
.footer-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 26px;
}

.navy-btn {
    background: #0e3a5f !important;
    color: #fff !important;
}

@media (max-width: 600px) {
    .footer-actions {
        flex-direction: column;
    }
}
</style>